<script setup lang="ts">
import { computed, ref } from 'vue';
import { mdiMagnify, mdiTuneVariant } from '@quasar/extras/mdi-v7';

interface OptionItem {
  label: string;
  value: string;
}

const props = withDefaults(
  defineProps<{
    statusOptions?: OptionItem[];
    showStatus?: boolean;
    showDateRange?: boolean;
  }>(),
  {
    statusOptions: () => [],
    showStatus: false,
    showDateRange: false,
  },
);

const searchTermModel = defineModel<string>('searchTerm', {
  default: '',
});
const statusModel = defineModel<string>('status', {
  default: '',
});
const fromDateModel = defineModel<string>('fromDate', {
  default: '',
});
const toDateModel = defineModel<string>('toDate', {
  default: '',
});

const isAdvancedVisible = ref(false);
const hasAdvancedFilters = computed(() => {
  return !!(props.showStatus || props.showDateRange);
});

function toggleAdvancedFilters() {
  const nextVisible = !isAdvancedVisible.value;
  isAdvancedVisible.value = nextVisible;

  if (!nextVisible) {
    statusModel.value = '';
    fromDateModel.value = '';
    toDateModel.value = '';
  }
}
</script>

<template>
  <div class="column q-gutter-sm">
    <div class="row q-col-gutter-sm q-row-gutter-sm items-center">
      <div class="col-12">
        <q-input
          v-model="searchTermModel"
          clearable
          outlined
          :dark="$q.dark.isActive"
          :class="$q.dark.isActive ? '' : 'bg-white text-dark'"
          :label="$t('SEARCH')"
        >
          <template #prepend>
            <q-icon :name="mdiMagnify" />
          </template>
          <template
            v-if="hasAdvancedFilters"
            #append
          >
            <q-btn
              round
              dense
              flat
              color="primary"
              :icon="mdiTuneVariant"
              :title="
                isAdvancedVisible
                  ? $t('HIDE_ADVANCED_SEARCH')
                  : $t('ADVANCED_SEARCH')
              "
              @click="toggleAdvancedFilters"
            >
              <q-tooltip>
                {{
                  isAdvancedVisible
                    ? $t('HIDE_ADVANCED_SEARCH')
                    : $t('ADVANCED_SEARCH')
                }}
              </q-tooltip>
            </q-btn>
          </template>
        </q-input>
      </div>
    </div>

    <div
      v-if="hasAdvancedFilters && isAdvancedVisible"
      class="row q-col-gutter-sm q-row-gutter-sm q-mt-xs"
    >
      <div
        v-if="props.showStatus"
        class="col-12"
        :class="props.showDateRange ? 'col-md-6' : 'col-md-12'"
      >
        <q-select
          v-model="statusModel"
          :options="props.statusOptions"
          emit-value
          map-options
          clearable
          outlined
          :dark="$q.dark.isActive"
          :class="$q.dark.isActive ? '' : 'bg-white text-dark'"
          :popup-content-class="$q.dark.isActive ? 'bg-dark text-white' : 'bg-white text-dark'"
          :label="$t('STATUS')"
        />
      </div>

      <template v-if="props.showDateRange">
        <div class="col-12 col-md-3">
          <q-input
            v-model="fromDateModel"
            type="date"
            outlined
            :dark="$q.dark.isActive"
          :class="$q.dark.isActive ? '' : 'bg-white text-dark'"
            :label="`${$t('FROM')} ${$t('DATE')}`"
          />
        </div>
        <div class="col-12 col-md-3">
          <q-input
            v-model="toDateModel"
            type="date"
            outlined
            :dark="$q.dark.isActive"
          :class="$q.dark.isActive ? '' : 'bg-white text-dark'"
            :label="`${$t('TO')} ${$t('DATE')}`"
          />
        </div>
      </template>
    </div>
  </div>
</template>
